import { useDocumentsRepository } from '@Hooks/useRepository';
import { ApiDynamicExportParameters } from '@Model';
import { DynamicExportRunRepository } from '@Repositories/Documents';
import { EnumMessageLevel, type FilterExpression, Result, useToasts } from '@synqo/frontend-component-library';
import { useMutation } from '@tanstack/react-query';

interface ExportParameters {
    exportDefinitionId: string;
    selectedIds?: string[];
    filter?: FilterExpression;
    fullTextSearch?: string;
}

/** Schedules an export run and returns its id. `announce` says in a toast that it runs in the background. */
export function useScheduleExportRun({ announce = true }: { announce?: boolean } = {}) {
    const exportRuns = useDocumentsRepository(DynamicExportRunRepository);
    const { pushAllToasts, pushToast } = useToasts();

    const { mutateAsync, isPending } = useMutation({
        mutationFn: async ({
            exportDefinitionId,
            selectedIds,
            filter,
            fullTextSearch,
        }: ExportParameters): Promise<Result<string>> => {
            const parameters: ApiDynamicExportParameters = {
                include_ids: selectedIds ?? [],
                filter: filter ?? undefined,
                full_text_search: fullTextSearch ?? '',
            };

            const response = await exportRuns.scheduleDynamicExportRun(exportDefinitionId, parameters);

            if (!response) {
                pushToast({ message: 'Fehler beim Exportieren.', title: 'Export', level: EnumMessageLevel.Danger });
                return { messages: [] } as unknown as Result<string>;
            }

            pushAllToasts(response.messages);
            return response;
        },
        onSuccess: () => {
            if (!announce) {
                return;
            }

            pushToast({
                message: 'Export wurde gestartet und wird im Hintergrund verarbeitet.',
                title: 'Export',
                level: EnumMessageLevel.Success,
            });
        },
    });

    return { isExporting: isPending, scheduleExport: mutateAsync };
}
